import { useState, useEffect } from 'react';
import { client } from '../../sdkAdapter';
import Swal from 'sweetalert2';
import MediaUploader from '../common/MediaUploader';
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import { Modal } from "../ui/modal";
import DatePicker from '../form/date-picker';
import Pagination from '../ui/pagination';
export default function PromotionsOffers() {
    const [offers, setOffers] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);

    // Pagination & Search State
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [searchTerm, setSearchTerm] = useState('');
    const [totalItems, setTotalItems] = useState(0);
    // Form State
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        code: '',
        price: 0,
        percentage_off: 0,
        total_off: 0,
        qty: 0,
        active: true,
        expiration_data: '',
        slug: '',
        image_id: 0,
        tags: [] as string[],
        products: [] as any[]
    });
    const [allProducts, setAllProducts] = useState<any[]>([]);
    const [allTags, setAllTags] = useState<any[]>([]);
    const fetchAllProducts = async () => {
        try {
            const res = await client.getApiCatalogProducts() as any;
            const data = res.data?.data?.data || res.data?.data || res.data || [];
            setAllProducts(Array.isArray(data) ? data : []);
        } catch (err) { console.error('Failed to fetch products:', err); }
    };
    const fetchAllTags = async () => {
        try {
            const res = await client.getApiCatalogTags() as any;
            const data = res.data?.data?.data || res.data?.data || res.data || [];
            setAllTags(Array.isArray(data) ? data : []);
        } catch (err) { console.error('Failed to fetch tags:', err); }
    };
    const refreshData = async () => {
        setLoading(true);
        try {
            const res = await client.getApiPromotionsOffers({}, {
                params: {
                    page: currentPage,
                    search: searchTerm,
                    per_page: rowsPerPage
                }
            }) as any;

            const responseBody = res.data;
            if (responseBody?.data?.data) {
                setOffers(responseBody.data.data);
                setTotalItems(responseBody.data.total || responseBody.data.data.length);
            } else {
                const legacyData = responseBody?.data || [];
                setOffers(Array.isArray(legacyData) ? legacyData : []);
                setTotalItems(Array.isArray(legacyData) ? legacyData.length : 0);
            }
        } catch (err) {
            console.error('Failed to fetch offers:', err);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        refreshData();
    }, [currentPage, rowsPerPage, searchTerm]);

    useEffect(() => {
        fetchAllProducts();
        fetchAllTags();
    }, []);
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target as any;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'number' ? Number(value) : (name === 'active' ? (value === '1') : value)
        }));
    };
    const handleEdit = (offer: any) => {
        setEditingId(offer.id);
        setFormData({
            title: offer.title || offer.name || '',
            description: offer.description || '',
            code: offer.code || '',
            price: Number(offer.price || 0),
            percentage_off: Number(offer.percentage_off || 0),
            total_off: Number(offer.total_off || 0),
            qty: Number(offer.qty || 0),
            active: Boolean(offer.active),
            expiration_data: offer.expiration_data || '',
            slug: offer.slug || '',
            image_id: Number(offer.image_id || 0),
            tags: Array.isArray(offer.tags) ? offer.tags : (offer.tags ? JSON.parse(offer.tags) : []),
            products: Array.isArray(offer.products) ? offer.products : []
        });
        setIsModalOpen(true);
    };
    const handleCreate = () => {
        setEditingId(null);
        setFormData({
            title: '',
            description: '',
            code: '',
            price: 0,
            percentage_off: 0,
            total_off: 0,
            qty: 0,
            active: true,
            expiration_data: '',
            slug: '',
            image_id: 0,
            tags: [],
            products: []
        });
        setIsModalOpen(true);
    };
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.expiration_data) {
            Swal.fire({
                title: 'Error',
                text: 'Expiration Date is required',
                icon: 'error'
            });
            return;
        }
        if (!formData.image_id) {
            Swal.fire({
                title: 'Error',
                text: 'Banner Image is required',
                icon: 'error'
            });
            return;
        }

        try {
            if (editingId) {
                await client.postApiPromotionsOfferUpdateId({
                    id: String(editingId),
                    postApiPromotionsOfferUpdateIdRequest: {
                        id: editingId,
                        title: formData.title,
                        description: formData.description,
                        code: formData.code,
                        price: formData.price,
                        percentage_off: formData.percentage_off,
                        total_off: formData.total_off,
                        qty: formData.qty,
                        active: formData.active,
                        expiration_data: formData.expiration_data,
                        slug: formData.slug,
                        image_id: formData.image_id,
                        tags: formData.tags,
                        products: formData.products.map((p: any) => p.id || p)
                    }
                });
            } else {
                await client.postApiPromotionsOfferCreate({
                    postApiPromotionsOfferCreateRequest: {
                        title: formData.title,
                        description: formData.description,
                        code: formData.code,
                        price: formData.price,
                        percentage_off: formData.percentage_off,
                        total_off: formData.total_off,
                        qty: formData.qty,
                        active: formData.active,
                        expiration_data: formData.expiration_data,
                        slug: formData.slug,
                        image_id: formData.image_id,
                        tags: formData.tags,
                        products: formData.products.map((p: any) => p.id || p)
                    } as any
                });
            }
            setIsModalOpen(false);
            setEditingId(null);
            refreshData();
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: editingId ? 'Offer updated successfully' : 'Offer created successfully',
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true
            });
        } catch (err) {
            console.error('Failed to save offer:', err);
            Swal.fire({
                title: 'Error',
                text: 'Failed to save offer',
                icon: 'error'
            });
        }
    };
    const handleDelete = async (id: number) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });
        if (!result.isConfirmed) return;
        try {
            await client.postApiPromotionsOfferDelete({
                postApiPromotionsOfferDeleteRequest: { id: id }
            } as any);
            refreshData();
        } catch (err) {
            console.error('Failed to delete offer:', err);
            // Fallback
            try {
                await client.postApiPromotionsOfferDelete({ id } as any);
                refreshData();
            } catch (retryErr) {
                Swal.fire({
                    title: 'Error',
                    text: 'Failed to delete offer',
                    icon: 'error'
                });
            }
        }
    };

    const totalPages = Math.ceil(totalItems / rowsPerPage);

    return (
        <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden shadow-sm">
            <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-white/[0.02]">
                <div>
                    <h3 className="text-lg font-bold text-gray-800 dark:text-white">Special Offers</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Manage special promotional offers and deals.</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search offers..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="pl-10 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all w-64"
                        />
                        <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                    {loading && <span className="text-xs text-gray-400 italic animate-pulse">Syncing...</span>}
                    <button
                        onClick={handleCreate}
                        className="px-5 py-2.5 bg-brand-500 text-white rounded-xl hover:bg-brand-600 transition-all text-sm font-bold shadow-lg shadow-brand-500/20 active:scale-95"
                    >
                        Create Offer
                    </button>
                </div>
            </div>
            <div className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-gray-50/30 dark:bg-white/[0.01]">
                            <TableCell isHeader className="px-6 py-4 text-start text-xs font-black uppercase tracking-widest text-gray-400">Title</TableCell>
                            <TableCell isHeader className="px-6 py-4 text-start text-xs font-black uppercase tracking-widest text-gray-400">Code</TableCell>
                            <TableCell isHeader className="px-6 py-4 text-start text-xs font-black uppercase tracking-widest text-gray-400">Price/Discount</TableCell>
                            <TableCell isHeader className="px-6 py-4 text-start text-xs font-black uppercase tracking-widest text-gray-400">Valid Until</TableCell>
                            <TableCell isHeader className="px-6 py-4 text-start text-xs font-black uppercase tracking-widest text-gray-400">Status</TableCell>
                            <TableCell isHeader className="px-6 py-4 text-end text-xs font-black uppercase tracking-widest text-gray-400">Actions</TableCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {offers.length === 0 && !loading ? (
                            <TableRow>
                                <TableCell colSpan={6} className="px-6 py-12 text-center text-gray-500">
                                    <div className="flex flex-col items-center gap-2">
                                        <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                                        </svg>
                                        <span className="font-medium text-lg">No offers found</span>
                                        <p className="text-sm max-w-[200px]">Start by creating your first special promotional offer.</p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            offers.map(item => (
                                <TableRow key={item.id} className="hover:bg-gray-50/50 dark:hover:bg-white/[0.02] transition-colors">
                                    <TableCell className="px-6 py-5 font-bold text-gray-900 dark:text-white">{item.title || item.name}</TableCell>
                                    <TableCell className="px-6 py-5 font-mono text-brand-500">{item.code || '-'}</TableCell>
                                    <TableCell className="px-6 py-5 font-medium">
                                        {item.price > 0 ? `$${item.price}` : (item.percentage_off ? `${item.percentage_off}% Off` : '-')}
                                    </TableCell>
                                    <TableCell className="px-6 py-5 text-sm text-gray-500">
                                        {item.expiration_data || '-'}
                                    </TableCell>
                                    <TableCell className="px-6 py-5">
                                        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${item.active ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'}`}>
                                            {item.active ? 'Active' : 'Inactive'}
                                        </span>
                                    </TableCell>
                                    <TableCell className="px-6 py-5 text-end">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => handleEdit(item)}
                                                className="p-2 text-brand-500 hover:bg-brand-50 rounded-lg transition-all"
                                                title="Edit Offer"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => handleDelete(item.id)}
                                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                                title="Delete Offer"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(rows) => {
                    setRowsPerPage(rows);
                    setCurrentPage(1);
                }}
                totalItems={totalItems}
            />

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} className="max-w-2xl h-[90vh]">
                <div className="flex flex-col h-full">

                    <div className="flex justify-between items-center p-6 border-b border-gray-100 dark:border-gray-700">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                            {editingId ? 'Edit Special Offer' : 'Create Special Offer'}
                        </h3>
                        <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>


                    <div className="p-6 overflow-y-auto flex-1 custom-scrollbar max-h-[60vh]">
                        <form id="offerForm" onSubmit={handleSubmit} className="space-y-5">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">

                                    <label className="block text-sm font-semibold mb-1.5 text-gray-700 dark:text-gray-300">
                                        Title <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="title"
                                        required
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                                        placeholder="Summer Sale"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-semibold mb-1.5 text-gray-700 dark:text-gray-300">Code  <span className="text-red-500">*</span> </label>
                                    <input
                                        type="text"
                                        name="code"
                                        required
                                        value={formData.code}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all dark:bg-gray-800 dark:border-gray-700 dark:text-white font-mono"
                                        placeholder="SUMMER25"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300">Price ($)</label>
                                    <input
                                        type="number"
                                        name="price"
                                        min="0"
                                        value={formData.price}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300">Quantity</label>
                                    <input
                                        type="number"
                                        name="qty"
                                        min="0"
                                        value={formData.qty}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300">Percentage Off (%)</label>
                                    <input
                                        type="number"
                                        name="percentage_off"
                                        min="0"
                                        max="100"
                                        value={formData.percentage_off}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300">Total Off ($)</label>
                                    <input
                                        type="number"
                                        name="total_off"
                                        min="0"
                                        value={formData.total_off}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-xs">
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300">Expiration Date  <span className="text-red-500">*</span></label>
                                    <DatePicker
                                        id="expiration_data"
                                        placeholder="Select expiration date"
                                        staticMode={false}
                                        defaultDate={formData.expiration_data ? new Date(formData.expiration_data) : undefined}
                                        onChange={(selectedDates) => {
                                            if (selectedDates.length > 0) {
                                                const d = selectedDates[0];
                                                const year = d.getFullYear();
                                                const month = String(d.getMonth() + 1).padStart(2, '0');
                                                const day = String(d.getDate()).padStart(2, '0');
                                                const val = `${year}-${month}-${day}`;
                                                setFormData(prev => ({ ...prev, expiration_data: val }));
                                            }
                                        }}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300">Status</label>
                                    <select
                                        name="active"
                                        value={formData.active ? '1' : '0'}
                                        onChange={handleInputChange as any}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                                    >
                                        <option value="1">Active</option>
                                        <option value="0">Inactive</option>
                                    </select>
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300">Promo Image</label>
                                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                                    <MediaUploader
                                        onUpload={(ids) => {
                                            if (ids.length > 0) {
                                                setFormData(prev => ({ ...prev, image_id: ids[0] }));
                                            }
                                        }}
                                        initialImages={formData.image_id ? [formData.image_id] : []}
                                        label="Upload Banner"
                                        folder="banners"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300">Slug  <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        name="slug"
                                        required
                                        value={formData.slug}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                                        placeholder="summer-offer-2025"
                                    />
                                </div>

                            </div>
                            <div className="space-y-1.5">
                                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300">Tags</label>

                                <div className="space-y-3">
                                    <select
                                        onChange={(e) => {
                                            const tagName = e.target.value;
                                            if (tagName && !formData.tags.includes(tagName)) {
                                                setFormData(prev => ({ ...prev, tags: [...prev.tags, tagName] }));
                                            }
                                        }}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                                    >
                                        <option value="">Select Tags...</option>
                                        {allTags.map(tag => (
                                            <option key={tag.id} value={tag.name}>{tag.name}</option>
                                        ))}
                                    </select>
                                    <div className="flex flex-wrap gap-2">
                                        {formData.tags.map(tagName => (
                                            <div key={tagName} className="flex items-center gap-2 px-3 py-1 bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400 rounded-lg text-xs font-bold border border-brand-100 dark:border-brand-900/50">
                                                <span>{tagName}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({ ...prev, tags: prev.tags.filter(x => x !== tagName) }))}
                                                    className="hover:text-red-500 font-black"
                                                >
                                                    &times;
                                                </button>
                                            </div>
                                        ))}
                                        {formData.tags.length === 0 && <span className="text-xs text-gray-500 italic px-1">No tags selected</span>}
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300">Products</label>
                                    <div className="space-y-3">
                                        <select
                                            onChange={(e) => {
                                                const p = allProducts.find(x => x.id === Number(e.target.value));
                                                if (p && !formData.products.find(x => x.id === p.id)) {
                                                    setFormData(prev => ({ ...prev, products: [...prev.products, p] }));
                                                }
                                            }}
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                                        >
                                            <option value="">Select Products...</option>
                                            {allProducts.map(p => (
                                                <option key={p.id} value={p.id}>{p.name} (${p.price})</option>
                                            ))}
                                        </select>
                                        <div className="flex flex-wrap gap-2">
                                            {formData.products.map(p => (
                                                <div key={p.id} className="flex items-center gap-2 px-3 py-1 bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400 rounded-lg text-xs font-bold border border-brand-100 dark:border-brand-900/50">
                                                    <span>{p.name}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData(prev => ({ ...prev, products: prev.products.filter(x => x.id !== p.id) }))}
                                                        className="hover:text-red-500 font-black"
                                                    >
                                                        &times;
                                                    </button>
                                                </div>
                                            ))}
                                            {formData.products.length === 0 && <span className="text-xs text-gray-500 italic px-1">No products selected</span>}
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300">Description  <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                                        placeholder="Special offer for our summer collection..."
                                    />
                                </div>
                            </div>

                        </form>
                    </div>
                    <div className="flex justify-end gap-3 p-6 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
                        <button
                            type="button"
                            onClick={() => setIsModalOpen(false)}
                            className="px-6 py-2.5 text-gray-600 font-semibold bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700 dark:hover:bg-gray-700"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="offerForm"
                            className="px-8 py-2.5 text-white font-bold bg-brand-500 rounded-xl hover:bg-brand-600 shadow-lg shadow-brand-500/20 transition-all flex items-center gap-2"
                        >
                            {editingId ? 'Update Offer' : 'Create Offer'}
                        </button>
                    </div>

                </div>
            </Modal>
        </div>
    );
}
